import { Group, Stack, Text, Title } from "@mantine/core"
import {
  IconCertificate,
  IconCpu,
  IconNeedle,
  IconRecycle,
  IconShirt,
  type Icon,
} from "@tabler/icons-react"

import { CommunicationStudioClasses } from "@tfg/types/constants/communication-studio.constant"
import type {
  AGECResponses,
  EcoModulationDisplayMention,
} from "@tfg/types/endpoints/agec"

import { useGetTranslationsContext } from "../context/translationsContext/useGetTranslationsContext"

const CRITERION_ICONS: Record<string, Icon> = {
  BONUS_DURABILITY: IconShirt,
  BONUS_ENVIRONMENTAL_LABELS: IconCertificate,
  BONUS_RECYCLED_MATERIALS: IconRecycle,
  MALUS_METALLOPLASTIC_FIBERS: IconNeedle,
  MALUS_EEE_COMPONENTS: IconCpu,
}

type EcoModulationProps = {
  agecData: AGECResponses.GenericAgecData
  showIcons?: boolean
}

const ICON_CLASS: Record<
  EcoModulationDisplayMention["kind"],
  (typeof CommunicationStudioClasses)[keyof typeof CommunicationStudioClasses]
> = {
  bonus: CommunicationStudioClasses.ECO_MODULATION_BONUS_ICON,
  malus: CommunicationStudioClasses.ECO_MODULATION_MALUS_ICON,
}

const CriterionRow = ({
  mention,
  title,
  showIcons,
}: {
  mention: EcoModulationDisplayMention
  title: string
  showIcons: boolean
}) => {
  const { t } = useGetTranslationsContext()
  const IconComponent = CRITERION_ICONS[mention.code] ?? IconShirt
  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      {showIcons ? (
        <IconComponent
          size={18}
          className={ICON_CLASS[mention.kind]}
          aria-hidden
          style={{ flexShrink: 0, marginTop: 2 }}
        />
      ) : null}
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Text
          className={CommunicationStudioClasses.ECO_MODULATION_CRITERION_TITLE}
        >
          {title}
        </Text>
        <Text className={CommunicationStudioClasses.ECO_MODULATION_SECONDARY}>
          {t(`ecoModulation.sentences.${mention.code}`)}
        </Text>
      </Stack>
    </Group>
  )
}

export const EcoModulation = ({
  agecData,
  showIcons = true,
}: EcoModulationProps) => {
  const { t } = useGetTranslationsContext()
  const mentions = agecData.ecoModulation?.mentions ?? []

  if (mentions.length === 0) {
    return null
  }

  const bonuses = mentions.filter((mention) => mention.kind === "bonus")
  const maluses = mentions.filter((mention) => mention.kind === "malus")

  return (
    <Stack className={CommunicationStudioClasses.ECO_MODULATION_CONTAINER}>
      <Title
        order={3}
        className={CommunicationStudioClasses.ECO_MODULATION_HEADER}
      >
        {t("ecoModulation.title")}
      </Title>
      {bonuses.length > 0 ? (
        <Stack gap="sm">
          {bonuses.map((mention) => (
            <CriterionRow
              key={mention.code}
              mention={mention}
              title={t(`ecoModulation.criteria.${mention.code}`)}
              showIcons={showIcons}
            />
          ))}
        </Stack>
      ) : null}
      {maluses.length > 0 ? (
        <Stack gap="sm">
          {maluses.map((mention) => (
            <CriterionRow
              key={mention.code}
              mention={mention}
              title={t(`ecoModulation.criteria.${mention.code}`)}
              showIcons={showIcons}
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  )
}
